import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Collapse,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import { CWY_COLORS } from '../../theme/colors';
import { PaymentSuccessCheck } from '../payment/PaymentSuccessCheck';
import { playPaymentSuccessSound } from '../../utils/playPaymentSuccessSound';
import {
  chatPaymentFooterLabel,
  formatPaymentAmount,
  payeeDisplayName,
  resolveChatPaymentDisplayStatus,
  type ChatPaymentDisplayStatus,
} from '../../utils/chatPaymentDisplay';

export interface ChatPaymentDetailsSheetProps {
  open: boolean;
  onClose: () => void;
  amount: number;
  currency: string;
  metadata: Record<string, unknown>;
  content?: string;
  timestampLabel?: string;
  /** Play success sound + animate tick when opening a newly completed payment */
  celebrateSuccess?: boolean;
}

function confirmationCopy(display: ChatPaymentDisplayStatus, payeeName: string): string {
  if (display === 'completed') {
    return `Payment should now be with ${payeeName}.`;
  }
  if (display === 'expired') {
    return 'This payment request is no longer available.';
  }
  return 'Tap Pay on the chat message to complete this payment securely with Stripe.';
}

export function ChatPaymentDetailsSheet({
  open,
  onClose,
  amount,
  currency,
  metadata,
  content,
  timestampLabel,
  celebrateSuccess = false,
}: ChatPaymentDetailsSheetProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const display = resolveChatPaymentDisplayStatus(
    metadata.payment_status as string | undefined,
    metadata.expires_at as string | undefined
  );
  const payeeName = payeeDisplayName(metadata);
  const statusLabel = chatPaymentFooterLabel(display);
  const note =
    metadata.note != null
      ? String(metadata.note)
      : content
        ? content.replace(/^Payment request:\s*/i, '')
        : '';

  const fullTimeLabel = useMemo(() => {
    if (timestampLabel) return timestampLabel;
    return undefined;
  }, [timestampLabel]);

  useEffect(() => {
    if (open && celebrateSuccess && display === 'completed') {
      playPaymentSuccessSound();
    }
  }, [open, celebrateSuccess, display]);

  useEffect(() => {
    if (!open) setMoreOpen(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 420,
          bgcolor: '#ffffff',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogContent sx={{ px: 2.5, pt: 2, pb: 3, bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Payment details</Typography>
          <IconButton aria-label="Close" onClick={onClose} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Amount card with center brightness */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            overflow: 'hidden',
            mb: 2.5,
            border: '1px solid',
            borderColor: display === 'completed' ? '#c8e6c9' : 'rgba(0,0,0,0.06)',
            py: 4,
            px: 2,
            textAlign: 'center',
            background:
              display === 'completed'
                ? `
                  radial-gradient(ellipse 70% 55% at 50% 45%, rgba(255,255,255,0.95) 0%, transparent 70%),
                  linear-gradient(160deg, #e8f5e9 0%, #fff8e7 45%, #e3f2fd 100%)
                `
                : `
                  radial-gradient(ellipse 70% 55% at 50% 45%, rgba(255,255,255,0.9) 0%, transparent 70%),
                  linear-gradient(160deg, #f5f5f5 0%, #fafafa 100%)
                `,
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: '50%',
              top: '48%',
              transform: 'translate(-50%, -50%)',
              width: 160,
              height: 90,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              fontWeight: 800,
              fontSize: '2.15rem',
              letterSpacing: '-0.03em',
              color: '#1a237e',
              lineHeight: 1.1,
              textShadow: '0 1px 0 rgba(255,255,255,0.8)',
            }}
          >
            {formatPaymentAmount(amount, currency)}
          </Typography>
        </Box>

        {display === 'completed' ? (
          <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', mb: 2.5 }}>
            <Box sx={{ flexShrink: 0, mt: 0.25 }}>
              <PaymentSuccessCheck size="sm" />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.5 }}>
              <Typography
                sx={{
                  color: CWY_COLORS.verified,
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                }}
              >
                Completed
              </Typography>
              <Typography sx={{ mt: 0.5, fontWeight: 500, color: CWY_COLORS.textPrimary }}>
                {confirmationCopy(display, payeeName)}
              </Typography>
              {fullTimeLabel ? (
                <Typography
                  variant="caption"
                  sx={{ color: CWY_COLORS.textFaint, display: 'block', mt: 0.5 }}
                >
                  {fullTimeLabel}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: display === 'expired' ? CWY_COLORS.textFaint : CWY_COLORS.textPrimary,
              }}
            >
              {statusLabel}
            </Typography>
            <Typography sx={{ mt: 0.5, color: CWY_COLORS.textSecondary }}>
              {confirmationCopy(display, payeeName)}
            </Typography>
          </Box>
        )}

        <Box
          component="button"
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: CWY_COLORS.textSecondary,
            p: 0,
            mb: 1.5,
            font: 'inherit',
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 22,
              transform: moreOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            View more details
          </Typography>
        </Box>

        <Collapse in={moreOpen}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.5,
            }}
          >
            <Avatar sx={{ bgcolor: '#eceff1', color: '#78909c', width: 44, height: 44 }}>
              <PersonOutlineIcon />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" sx={{ color: CWY_COLORS.textFaint }}>
                {display === 'completed' ? 'Sent to' : 'Pay to'}
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>{payeeName}</Typography>
              {fullTimeLabel ? (
                <Typography variant="caption" sx={{ color: CWY_COLORS.textFaint }}>
                  {fullTimeLabel}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Divider />

          <Box sx={{ py: 1.5 }}>
            <Typography variant="caption" sx={{ color: CWY_COLORS.textFaint, display: 'block' }}>
              Sent with
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>Card via Stripe</Typography>
          </Box>

          {metadata.transaction_id ? (
            <>
              <Divider />
              <Box sx={{ py: 1.5 }}>
                <Typography variant="caption" sx={{ color: CWY_COLORS.textFaint, display: 'block' }}>
                  Transaction ID
                </Typography>
                <Typography sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {String(metadata.transaction_id)}
                </Typography>
              </Box>
            </>
          ) : null}

          {note ? (
            <>
              <Divider />
              <Box sx={{ py: 1.5 }}>
                <Typography variant="caption" sx={{ color: CWY_COLORS.textFaint, display: 'block' }}>
                  Note
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{note}</Typography>
              </Box>
            </>
          ) : null}
        </Collapse>
      </DialogContent>
    </Dialog>
  );
}
